/**
 * Section Schema Registry
 * Centralized registry for section component element schemas
 */

import { validateSectionSchema } from './element-schema-validation'

import type { ElementSchema, SchemaRegistryEntry, SectionElementSchema } from '@/types/element-schema.type'

/**
 * Registry for managing section element schemas
 * Can be instantiated for testing or use singleton
 */
class SectionSchemaRegistry {
  private schemas: Map<string, SchemaRegistryEntry> = new Map()

  /**
   * Get the global singleton instance
   */
  public static getInstance(): SectionSchemaRegistry {
    if (!(globalThis as unknown as Record<string, unknown>).__sectionSchemaRegistry) {
      ;(globalThis as unknown as Record<string, unknown>).__sectionSchemaRegistry = new SectionSchemaRegistry()
    }
    return (globalThis as unknown as Record<string, unknown>).__sectionSchemaRegistry as SectionSchemaRegistry
  }

  /**
   * Register a section schema
   * @throws Error if schema validation fails
   */
  register(schema: SectionElementSchema): void {
    const validation = validateSectionSchema(schema)
    if (!validation.isValid) {
      throw new Error(`Invalid schema for ${schema.componentType}: ${validation.errors.map(e => e.message).join(', ')}`)
    }

    const existing = this.schemas.get(schema.componentType)
    const version = existing ? existing.version + 1 : 1

    this.schemas.set(schema.componentType, {
      schema,
      registeredAt: new Date().toISOString(),
      version,
    })
  }

  /**
   * Register multiple schemas at once
   */
  registerAll(schemas: SectionElementSchema[]): void {
    schemas.forEach(schema => this.register(schema))
  }

  /**
   * Get a schema by component type
   */
  get(componentType: string): SectionElementSchema | undefined {
    return this.schemas.get(componentType)?.schema
  }

  /**
   * Get the full registry entry with metadata
   */
  getEntry(componentType: string): SchemaRegistryEntry | undefined {
    return this.schemas.get(componentType)
  }

  /**
   * Get all registered schemas
   */
  getAll(): SectionElementSchema[] {
    return Array.from(this.schemas.values()).map(entry => entry.schema)
  }

  /**
   * Get all registry entries with metadata
   */
  getAllEntries(): SchemaRegistryEntry[] {
    return Array.from(this.schemas.values())
  }

  /**
   * Check if a schema is registered
   */
  has(componentType: string): boolean {
    return this.schemas.has(componentType)
  }

  /**
   * Remove a schema from the registry
   */
  remove(componentType: string): boolean {
    return this.schemas.delete(componentType)
  }

  /**
   * Clear all registered schemas
   */
  clear(): void {
    this.schemas.clear()
  }

  /**
   * Get the number of registered schemas
   */
  get size(): number {
    return this.schemas.size
  }

  /**
   * Get all registered component types
   */
  getComponentTypes(): string[] {
    return Array.from(this.schemas.keys())
  }

  /**
   * Find schemas by category
   */
  getByCategory(category: string): SectionElementSchema[] {
    return this.getAll().filter(schema => schema.category === category)
  }

  /**
   * Find schemas by tag
   */
  getByTag(tag: string): SectionElementSchema[] {
    return this.getAll().filter(schema => schema.tags?.includes(tag))
  }

  /**
   * Get a specific element from a schema
   */
  getElement(componentType: string, elementKey: string): ElementSchema | undefined {
    const schema = this.get(componentType)
    if (!schema) return undefined
    return schema.elements.find(el => el.key === elementKey)
  }

  /**
   * Export all schemas as JSON (for API serialization)
   */
  toJSON(): Record<string, SchemaRegistryEntry> {
    const result: Record<string, SchemaRegistryEntry> = {}
    this.schemas.forEach((entry, key) => {
      result[key] = entry
    })
    return result
  }
}

// Export singleton instance
export const sectionSchemaRegistry = SectionSchemaRegistry.getInstance()

// Export class for testing
export { SectionSchemaRegistry }

/**
 * Decorator function to register a component's schema
 * Usage: @RegisterSchema(schema) class MySection {}
 */
export function RegisterSchema(schema: SectionElementSchema) {
  return function <T extends { new (...args: unknown[]): unknown }>(constructor: T) {
    sectionSchemaRegistry.register(schema)
    return constructor
  }
}

/**
 * Helper to define schema on a component function
 * Usage: const MySection = () => {}; defineSchema(MySection, { ... })
 */
export function defineSchema<T extends (...args: unknown[]) => unknown>(component: T, schema: SectionElementSchema): T {
  ;(component as unknown as Record<string, unknown>).elementSchema = schema
  sectionSchemaRegistry.register(schema)
  return component
}
